import { supabase } from "./supabaseClient";

/**
 * Upload d'une ou plusieurs factures vers Supabase Storage
 * Gère les groupes d'upload et le versioning automatique
 */
export async function uploadInvoiceFiles(
  caseId: string,
  invoiceType: "deposit" | "final",
  files: File[]
): Promise<{ success: boolean; error?: string; uploadGroupId?: string }> {
  try {
    // 1. Déterminer le prochain numéro de version
    const { data: existingInvoices, error: fetchError } = await supabase
      .from("invoices")
      .select("version")
      .eq("case_id", caseId)
      .eq("invoice_type", invoiceType)
      .order("version", { ascending: false })
      .limit(1);

    if (fetchError) {
      console.error("Erreur lors de la récupération des factures existantes:", fetchError);
      return { success: false, error: fetchError.message };
    }

    const nextVersion = existingInvoices && existingInvoices.length > 0 
      ? existingInvoices[0].version + 1 
      : 1;

    // 2. Générer un UUID de groupe pour cet upload
    const uploadGroupId = crypto.randomUUID();

    // 3. Uploader chaque fichier
    const uploadPromises = files.map(async (file, index) => {
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(7);
      // Sanitize le nom de fichier (espaces, accents, parenthèses → underscores)
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const fileName = `${caseId}/${invoiceType}/${timestamp}-${randomSuffix}-${sanitizedName}`;

      // Upload vers Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("invoices")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error(`Erreur lors de l'upload du fichier ${file.name}:`, uploadError);
        throw new Error(`Upload échoué: ${uploadError.message}`);
      }

      // Obtenir l'URL publique
      const { data: urlData } = supabase.storage
        .from("invoices")
        .getPublicUrl(fileName);

      // Insérer dans la table invoices
      const { error: insertError } = await supabase.from("invoices").insert({
        case_id: caseId,
        invoice_type: invoiceType,
        file_url: urlData.publicUrl,
        upload_group_id: uploadGroupId,
        group_position: index + 1,
        version: nextVersion,
        status: "draft",
        amount: null, // À remplir manuellement
      });

      if (insertError) {
        console.error("Erreur lors de l'insertion dans la table invoices:", insertError);
        throw new Error(`Insertion échouée: ${insertError.message}`);
      }

      return true;
    });

    await Promise.all(uploadPromises);

    console.log(`✅ ${files.length} facture(s) uploadée(s) avec succès (version ${nextVersion})`);
    return { success: true, uploadGroupId };
  } catch (error) {
    console.error("Erreur lors de l'upload des factures:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Erreur inconnue" 
    };
  }
}

/**
 * Mettre à jour le montant d'une facture
 */
export async function updateInvoiceAmount(
  invoiceId: string,
  amount: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from("invoices")
      .update({ amount })
      .eq("id", invoiceId);

    if (error) {
      console.error("Erreur lors de la mise à jour du montant:", error);
      return { success: false, error: error.message };
    }

    console.log(`✅ Montant mis à jour: ${amount}€`);
    return { success: true };
  } catch (error) {
    console.error("Erreur lors de la mise à jour du montant:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Erreur inconnue" 
    };
  }
}

/**
 * Mettre à jour le montant pour tout un groupe (toutes les factures du même upload)
 */
export async function updateInvoiceGroupAmount(
  uploadGroupId: string,
  amount: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from("invoices")
      .update({ amount })
      .eq("upload_group_id", uploadGroupId);

    if (error) {
      console.error("Erreur lors de la mise à jour du montant du groupe:", error);
      return { success: false, error: error.message };
    }

    console.log(`✅ Montant du groupe mis à jour: ${amount}€`);
    return { success: true };
  } catch (error) {
    console.error("Erreur lors de la mise à jour du montant du groupe:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Erreur inconnue" 
    };
  }
}
